import { animation } from './consts/animation';
import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, ElementRef, HostListener, inject, Input, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { IconModule, InputtextModule, KeycloakService, MenuItem, ModalRef, ModalService } from '@atsystems/ui';
import { SimplebarAngularModule } from 'simplebar-angular';
import { getMenuItems } from 'src/app/common/consts/menu-items';
import { Roles } from 'src/app/common/helpers/roles';
import { convertLayout } from 'src/app/common/helpers/utils';
import { DEFAULT_MENU_ITEMS } from './consts/consts';
import { ProductTourService } from 'src/app/ui/services/product-tour/product-tour.service';

type SpotlightItem = MenuItem & { parentLabel?: string };

@Component({
  selector: 'ats-spotlight',
  imports: [
    CommonModule,
    IconModule,
    A11yModule,
    SimplebarAngularModule,
    ReactiveFormsModule,
    InputtextModule,
    RouterModule
  ],
  animations: [animation],
  templateUrl: './spotlight.component.html',
  styleUrl: './spotlight.component.sass',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpotlightComponent implements AfterViewInit {
  @ViewChild('modal') modal!: ElementRef<HTMLDivElement>;
  @ViewChild('input') input!: ElementRef<HTMLInputElement>;
  @ViewChild('resultsContainer') resultsContainer!: ElementRef<HTMLDivElement>;
  @Input() width: number = 500;

  private modalRef = inject(ModalRef);
  private cdr = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);
  private modalService = inject(ModalService);
  private keycloakService = inject(KeycloakService);
  private productTour = inject(ProductTourService);

  public windowWidth: number = window.innerWidth;
  public isClosing = false;
  public selectedIndex = signal<number>(0);
  public options = { autoHide: true };

  public menuItems: SpotlightItem[] = this.flattenMenuItems(getMenuItems(this))
    .filter(item => this.canView(item))
    .sort((a, b) => {
      if (a.route === 'tracker-v2') return -1;
      if (b.route === 'tracker-v2') return 1;
      return 0;
    });

  public defaultMenuItems: SpotlightItem[] = DEFAULT_MENU_ITEMS.filter(item => this.canView(item));

  public filterValue = signal<string | null>(null);
  public filteredMenuItems = computed<SpotlightItem[]>(() => {
    const filter = this.filterValue()?.toLowerCase()?.trim();
    if (!filter) return this.defaultMenuItems;

    const convertedFilter = convertLayout(filter);

    return this.menuItems.filter(item => {
      const haystacks = [
        item.label.toLowerCase(),
        item.parentLabel?.toLowerCase() ?? '',
        item.route?.toLowerCase() ?? '',
        ...(item.keywords ?? []).map(keyword => keyword.toLowerCase()),
      ];
      return haystacks.some(h => h.includes(filter) || h.includes(convertedFilter));
    });
  });

  public form = this.fb.group({
    value: this.fb.control(null),
  });

  private flattenMenuItems(items: MenuItem[], parentLabel?: string): SpotlightItem[] {
    return items.reduce((acc: SpotlightItem[], item) => {
      if (item.route || item.command) {
        acc.push({ ...item, parentLabel });
      }
      if (item.items?.length) {
        acc.push(...this.flattenMenuItems(item.items, item.label));
      }
      return acc;
    }, []);
  }

  private canView(item: MenuItem): boolean {
    return !item.roles?.length || this.keycloakService.hasResourceRole(item.roles);
  }

  public highlight(text: string | undefined, filter: string | null): string {
    if (!text) return '';
    const query = filter?.trim().toLowerCase();
    if (!query) return this.escapeHtml(text);

    const lower = text.toLowerCase();
    let matchLength = query.length;
    let index = lower.indexOf(query);
    if (index === -1) {
      const converted = convertLayout(query);
      index = lower.indexOf(converted);
      matchLength = converted.length;
    }
    if (index === -1) return this.escapeHtml(text);

    return this.escapeHtml(text.slice(0, index))
      + '<mark>' + this.escapeHtml(text.slice(index, index + matchLength)) + '</mark>'
      + this.escapeHtml(text.slice(index + matchLength));
  }

  private escapeHtml(text: string): string {
    return text.replace(/[&<>]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[char] ?? char));
  }

  constructor() {
    this.form.controls.value.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.filterValue.set(value);
      this.selectedIndex.set(0);
    });
    this.selectedIndex.set(0);

    this.productTour.registerAction('spotlight.demo-query', () => {
      this.form.controls.value.setValue('bundle' as never);
    });
    this.destroyRef.onDestroy(() => this.productTour.unregisterAction('spotlight.demo-query'));
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.input?.nativeElement) {
        this.input.nativeElement.focus();
      }
    }, 100);

    setTimeout(() => {
      document.addEventListener('click', this.onDocumentClick.bind(this));
    }, 100);
  }

  onDocumentClick(event: Event) {
    const target = event.target as Element | null;
    if (this.modal?.nativeElement?.contains(target as Node)) return;
    if (target?.closest?.('.driver-popover')) return;
    this.close();
  }

  @HostListener('keydown', ['$event'])
  public handleKeyDown(event: KeyboardEvent) {
    if (event.code === 'Escape') {
      this.close();
    }
    if (event.code === 'Enter') {
      const newTab = event.shiftKey || event.metaKey || event.ctrlKey;
      event.preventDefault();
      event.stopPropagation();
      this.openLink(event, this.filteredMenuItems()[this.selectedIndex()], newTab);
    }
    if (event.code === 'ArrowDown') {
      event.preventDefault();
      const newIndex = this.selectedIndex() < this.filteredMenuItems().length - 1
        ? this.selectedIndex() + 1
        : 0;
      this.selectedIndex.set(newIndex);
      this.scrollToSelected();
    }

    if (event.code === 'ArrowUp') {
      event.preventDefault();
      const newIndex = this.selectedIndex() > 0
        ? this.selectedIndex() - 1
        : this.filteredMenuItems().length - 1;
      this.selectedIndex.set(newIndex);
      this.scrollToSelected();
    }
  }

  public openLink(event: MouseEvent | KeyboardEvent, menuItem: MenuItem, newTab: boolean = false): void {
    event.preventDefault();
    event.stopPropagation();

    if (menuItem.route) {
      const isNewTab = newTab ||
        (event instanceof MouseEvent && event.metaKey) ||
        (event instanceof KeyboardEvent && event.metaKey);

      if (isNewTab) {
        window.open(menuItem.route, '_blank');
      } else {
        this.router.navigate([menuItem.route]);
      }
      this.modalRef.close();
    } else if (menuItem.command) {
      menuItem.command();
      this.modalRef.close();
    }
  }

  public close(): void {
    if (this.isClosing) return;
    this.isClosing = true;

    setTimeout(() => {
      this.modalRef.close();
      this.cdr.markForCheck();
    }, 150);
  }

  public async openApiKeysModal(): Promise<void> {
    if (!this.keycloakService.hasResourceRole(Roles.API_KEY_VIEWER)) return;
    const { ApiKeysComponent } = await import('src/app/pages/api-keys/api-keys.component');
    this.modalService.open(ApiKeysComponent);
  }

  private scrollToSelected(): void {
    setTimeout(() => {
      const container = this.resultsContainer?.nativeElement;
      if (!container) return;

      const selectedElement = container.querySelector(`.ats-spotlight-item:nth-child(${this.selectedIndex() + 1})`) as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }, 0);
  }
}
